import { Alert, Box, Button, Typography } from "@mui/material";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiGoogleLogin, login } from "../../sevices/apiAuth";
import GraphicElements from "../../styles/svg/graphic_elements.svg";
import { localStorageInstance } from "../../utils/localStorage";
import { useDispatch } from "react-redux";
import { user } from "../../redux/usersSlice";
import { useResponsive } from "../../hooks/useResponsive";
import mobileBg from "../../styles/svg/login_bg_mobile.svg";
import { useAssets } from "../../utils/reactQuery/domainAssets";
import { getAPIBaseUrl } from "../../utils/constants";
import TenantSiteName from "../../components/TenantSiteName";
import "./auth.scss";
import { CustomButton } from "../../components/Button/Button";
import { RFHTextField } from "../../components/RFH";
import { yupResolver } from "@hookform/resolvers/yup";
import { loginSchema } from "../../schemas/loginSchema";
import googleIcon from "../../styles/svg/ic_google.svg";
import { Divider } from "@mui/material";
import SubscriptionExpiredPopup from "../../components/SubscriptionExpiredPopup";
import { subscriptionManager } from "../../utils/subscriptionManager";

function Login({ onLogin }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loginWithEmail, setLoginWith] = React.useState(false);
  const [apiError, setApiError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = React.useState(false);
  const queryParamsData = searchParams.get("data");
  const errorFromQueryParams = searchParams.get("error");
  const { register, handleSubmit, reset, ...methods } = useForm({
    defaultValues: {},
    resolver: yupResolver(loginSchema),
  });

  if (errorFromQueryParams) {
    alert(errorFromQueryParams, { duration: 8000 });
  }

  React.useEffect(() => {
    if (errorFromQueryParams) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("error");
      setSearchParams(newSearchParams);
    }
  }, [errorFromQueryParams]);

  // Subscribe to subscription expiration events from subscription manager
  React.useEffect(() => {
    const unsubscribe = subscriptionManager.subscribe((isExpired) => {
      if (isExpired) {
        setShowSubscriptionPopup(true);
      } else {
        setShowSubscriptionPopup(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const isMobile = useResponsive("down", "md");
  const { logo } = useAssets();
  const apiUrl = getAPIBaseUrl();

  const url =
    localStorageInstance.getItem("redirectLocation") ||
    searchParams.get("url") ||
    "/my-task?tab=mine";

  const setDetails = (data) => {
    dispatch(user(data.user_data));
    localStorageInstance.setItem({
      key: "token",
      value: data.token.access_token,
    });
    localStorageInstance.setItem({
      key: "role",
      value: data.user_data.role,
    });
    localStorageInstance.setItem({
      key: "userId",
      value: data.user_data.user_id,
    });
    localStorageInstance.setItem({
      key: "refresh_token",
      value: data.token.refresh_token,
    });
  };

  function createFormData(data) {
    const formData = new FormData();
    formData.append("username", data.email);
    formData.append("password", data.password);
    return formData;
  }

  const isEmail = (input) => {
    const emailRegex = /^[a-zA-Z0-9._%+!*-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(input);
  };

  React.useEffect(() => {
    const decodedData = queryParamsData
      ? JSON.parse(atob(queryParamsData))
      : null;
    if (queryParamsData) {
      localStorageInstance.setItem({
        key: "token",
        value: decodedData.access_token,
      });
      localStorageInstance.setItem({
        key: "refresh_token",
        value: decodedData.refresh_token,
      });
      localStorageInstance.setItem({
        key: "role",
        value: decodedData.role,
      });
      localStorageInstance.setItem({
        key: "userId",
        value: decodedData.user_id,
      });
      navigate(url);
    }
  }, [queryParamsData]);

  const handleGoogleLogin = () => {
    apiGoogleLogin()
      .then((res) => {
        window.location.href = res?.data?.auth_url;
      })
      .catch((err) => {
        console.log("google login error ==== ", err);
        // Handle 423 status code (subscription expired)
        if (err?.response?.status === 423) {
          subscriptionManager.notifyExpired();
        }
      });
  };

  const handleClosePopup = () => {
    setShowSubscriptionPopup(false);
  };
  const submit = (data) => {
    setApiError("");
    setLoading(true);
    const formData = createFormData(data);

    if (isEmail(data.email)) {
      login(formData, true)
        .then((res) => {
          let data = res.data.data.responseData[0];
          // setDetails(data);
          // navigate(url);
          onLogin({ ...data, lastLogin: data?.user_data?.last_seen }, url);
        })
        .catch((err) => {
          console.log("login error ==== ", err);
          // Handle 423 status code (subscription expired)
          if (err?.response?.status === 423) {
            subscriptionManager.notifyExpired();
            return;
          }
          if (err.code == "ERR_NETWORK") {
            setApiError("An unexpected error occurred. Please try again later");
          } else setApiError(err.response?.data?.message || "An error occurred");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      login(formData, false)
        .then((res) => {
          let data = res.data.data.responseData[0];
          setDetails(data);
          navigate(url);
        })
        .catch((err) => {
          console.log(err);
          // Handle 423 status code (subscription expired)
          if (err?.response?.status === 423) {
            subscriptionManager.notifyExpired();
            return;
          }
          if (err.code == "ERR_NETWORK") {
            setApiError("An unexpected error occurred. Please try again later");
          } else setApiError(err.response?.data?.message || "An error occurred");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  function onError(errors) {
    console.log(errors);
  }

  function changeLoginWith() {
    reset();
    setLoginWith((loginWith) => !loginWith);
    setApiError("");
  }

  const handleForgotePassword = () => {
    navigate("/forgotpassword");
  };

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${isMobile ? mobileBg : GraphicElements})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "auto",
        minHeight: "100vh",
      }}
      disableGutters={true}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: 440,
          justifyContent: "center",
          padding: "40px 15px",
        }}
      >
        {logo ? (
          <img
            src={`${apiUrl}/${logo}`}
            alt="not found"
            width={isMobile ? 251 : ""}
            className="login_logo tenant-heading-logo"
          />
        ) : (
          <TenantSiteName className="tenant-heading-logo" />
        )}
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit(submit, onError)}
            noValidate
            className="login-form"
          >
            <Typography
              className="text-center black-primary-color mb-4 weight-700"
              component={"h1"}
              variant="h5"
            >
              Welcome to Task Board
            </Typography>
            {apiError && <Alert severity="error">{apiError}</Alert>}

            <RFHTextField
              margin="normal"
              required
              fullWidth
              id="email"
              // label={loginWithEmail ? "Email Address" : "Username"}
              label={"Username / Email"}
              name="email"
              autoComplete="email"
              className="primary-input-field"
              defaultPadding={false}
            />
            <RFHTextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              defaultPadding={false}
            />

            <div className=" forgot-password-div">
              <span className="card-anchor" onClick={handleForgotePassword}>
                Forgot Password
              </span>
            </div>

            <CustomButton
              type="submit"
              fullWidth
              variant="contained"
              className="btn-blue"
              sx={{ mt: "14px", padding: "13px 16px" }}
              disabled={loading}
            >
              Sign In
            </CustomButton>
            <Divider sx={{ mt: "24px", mb: "8px", color: "#919EAB" }}>
              OR
            </Divider>
            <Button
              startIcon={
                <img src={googleIcon} alt="" height="20px" width="20px" />
              }
              variant="outlined"
              size="large"
              className="mt-4 google-login-btn"
              fullWidth
              onClick={handleGoogleLogin}
              disabled={false}
            >
              Sign in with Google
            </Button>
            {/* <Divider sx={{ mt: "24px", mb: "24px", color: "#919EAB" }}>
            OR
          </Divider>
          <Button
            fullWidth
            variant="outlined"
            className="btn-secondary-border"
            sx={{
              padding: "13px 16px",
            }}
            onClick={changeLoginWith}>
            {loginWithEmail ? "Login with LDAP" : "Login with Client"}
          </Button> */}
          </Box>
        </FormProvider>
      </Box>
      <SubscriptionExpiredPopup
        open={showSubscriptionPopup}
        onClose={handleClosePopup}
        disableClose={true}
      />
    </Box>
  );
}

export default Login;
